import { FC } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Popconfirm, Row, Typography } from 'antd';
import { TbCurrencyDollar, TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    FinancialValueDisplayControl,
    PreferenceRow,
    ThemeDropdown,
    TimezoneDropdown,
    useAccountFinancialValueDisplayPreference,
    useFinancialValueDisplay,
    useUpdateAccountFinancialValueDisplayPreference,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { clearLocalStorageBesidesAuthToken } from '@/utilities';

const AccountFinancialValuePreference: FC = () => {
    const { preference: resolvedValue } = useFinancialValueDisplay();
    const { isLoading, preference } = useAccountFinancialValueDisplayPreference();
    const updatePreference = useUpdateAccountFinancialValueDisplayPreference();

    return (
        <FinancialValueDisplayControl
            allowInherit
            disabled={isLoading || updatePreference.isPending}
            inheritLabel='Use Workspace Default'
            loading={updatePreference.isPending}
            resolvedValue={resolvedValue}
            value={preference}
            onChange={async (value) => {
                try {
                    await updatePreference.mutateAsync(value);
                    toast.success(value ? 'Financial display preference updated' : 'Workspace default restored');
                } catch (error) {
                    captureException(error, { extra: { preference: value } });
                    toast.error('Unable to update financial display preference');
                }
            }}
        />
    );
};

const Overview: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <>
            <Typography.Title level={4}>Theme & Timezone</Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Set your theme and timezone preferences.
            </Typography.Paragraph>
            <Row gutter={[8, 8]} style={{ marginTop: '1rem' }}>
                <Col span={12}>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Theme</small>
                    </div>
                    <ThemeDropdown />
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Preferred Timezone</small>
                    </div>
                    <TimezoneDropdown />
                </Col>
            </Row>
            <Divider />
            <section
                aria-label='Financial Value Display'
                style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    overflow: 'hidden',
                }}
            >
                <PreferenceRow
                    control={<AccountFinancialValuePreference />}
                    description='Choose how large financial values are presented throughout research surfaces. Abbreviated values always retain an exact-value disclosure, and this setting never changes stored or exported data.'
                    icon={<TbCurrencyDollar size='1rem' />}
                    label='Financial Value Display'
                />
            </section>
            <Divider />
            <Typography.Title level={4}>Reset Preferences</Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Reset all workspace preferences back to their defaults.
            </Typography.Paragraph>
            <Popconfirm
                cancelText='Cancel'
                okText='Reset'
                overlayStyle={{ maxWidth: 300 }}
                placement='right'
                title='Reset your workspace preferences?'
                onConfirm={() => {
                    clearLocalStorageBesidesAuthToken();
                    toast('Preferences Reset', {
                        icon: <TbRefresh size='1rem' />,
                        description: 'Refresh the page to see the defaults.',
                    });
                }}
            >
                <Button block icon={<TbRefresh size='1rem' />} type='dashed'>
                    Reset Workspace Preferences
                </Button>
            </Popconfirm>
        </>
    );
};

export default Overview;
